import type { FormEvent, ReactNode } from "react";
import { Search } from "lucide-react";
// eslint-disable-next-line import/no-unresolved
import { Card } from "@astrana/ui/base";
import { DatePicker } from "../components/DatePicker";
import type { MemberSearchResult } from "./graphql/memberSearch";
import { MemberResultsTable, type SearchFilters } from "./MemberResultsTable";

/**
 * The member search card: the Home widget and the full `/members` page both
 * render THIS, so the two can no longer drift apart in style.
 *
 * Presentational only. Each caller owns its own state and search call,
 * because the page also carries the EHR bridge (auto-search, auto-navigate,
 * the in-context chart) and the widget deliberately does not — mounting that
 * logic twice would race `autoNav`'s module-level guard. See the headers of
 * `MemberSearchWidget` and `MemberSearchPage`.
 *
 * What differs between the two callers is passed in, not forked:
 *   - `resultsMaxHeightClass`: Home caps the list and scrolls it in place;
 *     the page lets it run.
 *   - `footer`: Home's "Open in full search" link.
 */
export interface MemberSearchCardProps {
  /** Prefix for the field ids, so two mounted cards never share an id. */
  idPrefix: string;
  filters: SearchFilters;
  onFieldChange: (key: keyof SearchFilters, value: string) => void;
  showIneligible: boolean;
  /** Callers re-run the current search here, so the rows update on flip. */
  onShowIneligibleChange: (next: boolean) => void;
  onSubmit: (e: FormEvent) => void;
  onClear: () => void;
  loading: boolean;
  validationError: string | null;
  error?: Error | null;
  /** The last submitted search; null until the first one. */
  submitted: SearchFilters | null;
  results: MemberSearchResult[];
  /**
   * Provider Portal logins must pair a name search with a Date of Birth (PP's
   * own search rule). States that up front and marks the DOB field required.
   */
  requiresDob?: boolean;
  resultsMaxHeightClass?: string;
  footer?: ReactNode;
}

const fieldClass =
  "min-w-0 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground " +
  "placeholder:text-muted-foreground hover:border-ring focus:border-primary focus:outline-none " +
  "focus:ring-2 focus:ring-primary/15 transition-all duration-150";

const labelClass =
  "text-[10px] font-semibold uppercase tracking-wide text-foreground/80";

export function MemberSearchCard({
  idPrefix,
  filters,
  onFieldChange,
  showIneligible,
  onShowIneligibleChange,
  onSubmit,
  onClear,
  loading,
  validationError,
  error,
  submitted,
  results,
  requiresDob = false,
  resultsMaxHeightClass,
  footer,
}: MemberSearchCardProps) {
  const hasSearched = submitted !== null;
  const errorId = `${idPrefix}-search-error`;

  return (
    // `display: contents` scopes the library's `important: ".astrana-ui"`
    // utilities to this subtree without inserting a layout box, so `Card`
    // stays its parent's direct item — same wrapper the panel cards use
    // internally, which bare `Card` doesn't supply itself.
    <div className="astrana-ui" style={{ display: "contents" }}>
      <Card
        variant="plain"
        header={
          <>
            <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span>Member Search</span>
            {hasSearched && !loading && results.length > 0 ? (
              <span className="ml-auto text-xs font-normal text-muted-foreground">
                {results.length} {results.length === 1 ? "match" : "matches"}
              </span>
            ) : null}
          </>
        }
      >
        <form
          onSubmit={onSubmit}
          className="flex flex-col gap-3"
          aria-describedby={validationError ? errorId : undefined}
        >
          {/* The search rule stated UP FRONT. `buildFilter` drops a lone
              first or last name, so a half-filled name searches on nothing —
              the same rule the validation error below repeats on a failed
              submit, said once before the user can trip on it. Provider
              Portal logins also need a DOB with a name (validateMemberSearch). */}
          <p className="m-0 text-xs text-muted-foreground">
            {requiresDob
              ? "Enter a first and last name (2+ characters each) with a Date of Birth, or search by Member ID."
              : "Enter a first and last name (2+ characters each), or search by Member ID."}
          </p>

          {/* 2x2 while the card is narrow (the extension side panel), one
              4-across row at `lg:`. `lg:` is the SAME breakpoint HomePage's
              lower grid splits into columns at, so on Home the card widens and
              these fields spread in one step — at full width a 2x2 leaves each
              field ~half the page, which reads as a stretched form rather
              than a search bar. */}
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <Field
              id={`${idPrefix}-firstName`}
              label="First Name"
              placeholder="e.g. John"
              value={filters.firstName}
              onChange={(v) => onFieldChange("firstName", v)}
            />
            <Field
              id={`${idPrefix}-lastName`}
              label="Last Name"
              placeholder="e.g. Doe"
              value={filters.lastName}
              onChange={(v) => onFieldChange("lastName", v)}
            />
            <Field
              id={`${idPrefix}-mpi`}
              label="Member ID"
              placeholder="Health Plan ID or MBI"
              value={filters.mpi}
              onChange={(v) => onFieldChange("mpi", v)}
            />
            <div className="flex flex-col gap-1">
              <label htmlFor={`${idPrefix}-dob`} className={labelClass}>
                Date of Birth
                {requiresDob ? (
                  <span aria-hidden="true" className="text-destructive">
                    {" "}*
                  </span>
                ) : null}
              </label>
              {/* `size="compact"` matches the fields beside it. The calendar
                  panel is portaled to the body — without that it would be
                  clipped by `Card`'s `overflow-hidden` rounded edge. */}
              <DatePicker
                id={`${idPrefix}-dob`}
                size="compact"
                value={filters.dob}
                onChange={(v) => onFieldChange("dob", v)}
                ariaLabel="Date of Birth"
              />
            </div>
          </div>

          {/* Toggle and buttons PAIR UP into one row at `lg:`. Stacked, a
              `justify-between` toggle would strand its switch ~1000px from
              its own label at the far right edge, with the buttons orphaned
              on a row below. Below `lg:` this wrapper is inert. */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            {/* The switch leads and the label follows, adjacent at EVERY
                width, so the control reads as one unit however wide the card
                gets. */}
            <div className="flex items-center justify-start gap-2">
              <button
                type="button"
                role="switch"
                aria-checked={showIneligible}
                aria-label="Show ineligible members"
                onClick={() => onShowIneligibleChange(!showIneligible)}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${showIneligible ? "bg-primary" : "bg-input"}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background shadow transition-transform ${showIneligible ? "translate-x-[1.125rem]" : "translate-x-0.5"}`}
                />
              </button>
              <span className="text-xs font-medium text-foreground">
                Show ineligible members
              </span>
            </div>

            {validationError ? (
              <p id={errorId} className="m-0 text-xs text-destructive" role="alert">
                {validationError}
              </p>
            ) : null}
            {error ? (
              <p className="m-0 text-xs text-destructive" role="alert">
                <span className="font-semibold">Search failed:</span> {error.message}
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClear}
                className="rounded-md px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                Clear
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex shrink-0 items-center justify-center rounded-md bg-primary px-3.5 py-1.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? "Searching…" : "Search"}
              </button>
            </div>
          </div>
        </form>

        {/* Results. NOT full-bleed like the panel cards' hairline rows: they
            want the body's normal inset. One hairline separates them from the
            form above. With `resultsMaxHeightClass` the list scrolls INSIDE
            the card; `overscroll-contain` stops a flick at the end of the
            list from chaining into the page's own scroll container, and the
            footer sits OUTSIDE the scroll box so it stays reachable. */}
        {hasSearched ? (
          <div className="mt-3 border-t border-border pt-3">
            {loading || results.length > 0 ? (
              <div
                className={
                  resultsMaxHeightClass
                    ? `${resultsMaxHeightClass} overflow-y-auto overscroll-contain pr-1`
                    : undefined
                }
              >
                <MemberResultsTable
                  results={results}
                  loading={loading}
                  hasSearched={hasSearched}
                  searchFilters={submitted ?? undefined}
                />
              </div>
            ) : error ? null : (
              // A failed search has no results to report, so the empty state
              // would contradict the "Search failed" line above.
              <p className="m-0 py-1 text-[12.5px] text-muted-foreground">
                No members found. Try adjusting your search.
              </p>
            )}
            {results.length > 0 && !loading ? footer : null}
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function Field({
  id,
  label,
  placeholder,
  value,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={fieldClass}
      />
    </div>
  );
}
